import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import {
  KanbanCampanhasColumnLabels,
  KanbanColumnLabels,
  KanbanPosVendasColumnLabels,
  KanbanVendasColumnLabels,
  PosVendasFinalizadoInboundRoute,
  PosVendasSettings,
} from '../../types';
import './KanbanSettingsTab.css';

interface KanbanSettingsTabProps {
  isOwner: boolean;
  isAdmin: boolean;
  organizationId: string;
}

const DEFAULT_ROUTE: PosVendasFinalizadoInboundRoute = 'vendas_aguardando_contato';

const VENDAS_DEFAULTS = {
  aguardandoContato: 'Aguardando Contato',
  emAtendimento: 'Em Atendimento',
  emNegociacao: 'Em Negociação',
  urgentes: 'Urgente',
  proposta: 'Proposta',
  fechamento: 'Fechamento',
} as const satisfies Record<
  Exclude<keyof KanbanVendasColumnLabels, 'inativos' | 'backlog'>,
  string
>;

const POS_VENDAS_DEFAULTS: Required<KanbanPosVendasColumnLabels> = {
  conversoesGanhas: 'Conversões Ganhas',
  emAtendimento: 'Em Atendimento',
  aguardandoRetorno: 'Aguardando Retorno',
  finalizado: 'Finalizado',
};

const CAMPANHAS_DEFAULTS: Required<KanbanCampanhasColumnLabels> = {
  pendente: 'Pendente',
  emExecucao: 'Em Execução',
  finalizado: 'Finalizado',
};

function labelsToFormState(labels: KanbanColumnLabels) {
  return {
    vendas: {
      aguardandoContato: labels.vendas?.aguardandoContato ?? '',
      emAtendimento: labels.vendas?.emAtendimento ?? '',
      emNegociacao: labels.vendas?.emNegociacao ?? '',
      urgentes: labels.vendas?.urgentes ?? labels.vendas?.inativos ?? '',
      proposta: labels.vendas?.proposta ?? '',
      fechamento: labels.vendas?.fechamento ?? '',
    },
    posVendas: {
      conversoesGanhas: labels.posVendas?.conversoesGanhas ?? '',
      emAtendimento: labels.posVendas?.emAtendimento ?? '',
      aguardandoRetorno: labels.posVendas?.aguardandoRetorno ?? '',
      finalizado: labels.posVendas?.finalizado ?? '',
    },
    campanhas: {
      pendente: labels.campanhas?.pendente ?? '',
      emExecucao: labels.campanhas?.emExecucao ?? '',
      finalizado: labels.campanhas?.finalizado ?? '',
    },
  };
}

type LabelsFormState = ReturnType<typeof labelsToFormState>;

function formStateToPayload(state: LabelsFormState): KanbanColumnLabels {
  const trim = (v: string) => v.trim() || undefined;
  return {
    vendas: {
      aguardandoContato: trim(state.vendas.aguardandoContato),
      emAtendimento: trim(state.vendas.emAtendimento),
      emNegociacao: trim(state.vendas.emNegociacao),
      urgentes: trim(state.vendas.urgentes),
      proposta: trim(state.vendas.proposta),
      fechamento: trim(state.vendas.fechamento),
    },
    posVendas: {
      conversoesGanhas: trim(state.posVendas.conversoesGanhas),
      emAtendimento: trim(state.posVendas.emAtendimento),
      aguardandoRetorno: trim(state.posVendas.aguardandoRetorno),
      finalizado: trim(state.posVendas.finalizado),
    },
    campanhas: {
      pendente: trim(state.campanhas.pendente),
      emExecucao: trim(state.campanhas.emExecucao),
      finalizado: trim(state.campanhas.finalizado),
    },
  };
}

export const KanbanSettingsTab: React.FC<KanbanSettingsTabProps> = ({
  isOwner,
  isAdmin,
  organizationId,
}) => {
  const canEdit = isOwner || isAdmin;

  const emptyForm = labelsToFormState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingLabels, setSavingLabels] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [labelsSuccessMsg, setLabelsSuccessMsg] = useState<string | null>(null);
  const [labelsError, setLabelsError] = useState<string | null>(null);
  const [route, setRoute] = useState<PosVendasFinalizadoInboundRoute>(DEFAULT_ROUTE);
  const [initialRoute, setInitialRoute] = useState<PosVendasFinalizadoInboundRoute>(DEFAULT_ROUTE);
  const [labels, setLabels] = useState<LabelsFormState>(emptyForm);
  const [initialLabels, setInitialLabels] = useState<LabelsFormState>(emptyForm);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [settings, columnLabels] = await Promise.all([
        apiService.getPosVendasSettings(organizationId),
        apiService.getKanbanColumnLabels(organizationId),
      ]);
      const current = settings.finalizadoInboundRoute || DEFAULT_ROUTE;
      setRoute(current);
      setInitialRoute(current);
      const form = labelsToFormState(columnLabels);
      setLabels(form);
      setInitialLabels(form);
    } catch (err) {
      console.error('Failed to load Kanban settings:', err);
      setError('Não foi possível carregar as configurações. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    if (!canEdit) return;
    try {
      setSaving(true);
      setError(null);
      setSuccessMsg(null);
      const payload: PosVendasSettings = { finalizadoInboundRoute: route };
      const updated = await apiService.updatePosVendasSettings(organizationId, payload);
      const saved = updated.finalizadoInboundRoute || DEFAULT_ROUTE;
      setRoute(saved);
      setInitialRoute(saved);
      setSuccessMsg('Configurações salvas.');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      console.error('Failed to save Kanban settings:', err);
      setError('Falha ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLabels = async () => {
    if (!canEdit) return;
    try {
      setSavingLabels(true);
      setLabelsError(null);
      setLabelsSuccessMsg(null);
      const payload = formStateToPayload(labels);
      const updated = await apiService.updateKanbanColumnLabels(organizationId, payload);
      const form = labelsToFormState(updated);
      setLabels(form);
      setInitialLabels(form);
      setLabelsSuccessMsg('Nomes das colunas salvos.');
      setTimeout(() => setLabelsSuccessMsg(null), 3000);
    } catch (err) {
      console.error('Failed to save kanban column labels:', err);
      setLabelsError('Falha ao salvar os nomes. Tente novamente.');
    } finally {
      setSavingLabels(false);
    }
  };

  const setVendasLabel = (key: keyof LabelsFormState['vendas'], value: string) =>
    setLabels((prev) => ({ ...prev, vendas: { ...prev.vendas, [key]: value } }));

  const setPosVendasLabel = (key: keyof LabelsFormState['posVendas'], value: string) =>
    setLabels((prev) => ({ ...prev, posVendas: { ...prev.posVendas, [key]: value } }));

  const setCampanhasLabel = (key: keyof LabelsFormState['campanhas'], value: string) =>
    setLabels((prev) => ({ ...prev, campanhas: { ...prev.campanhas, [key]: value } }));

  const dirty = route !== initialRoute;
  const labelsDirty = JSON.stringify(labels) !== JSON.stringify(initialLabels);

  if (loading) {
    return <div className="kanban-settings-loading">Carregando configurações…</div>;
  }

  return (
    <div className="kanban-settings-tab">
      <div className="kanban-settings-header">
        <h2>Configurações do Kanban</h2>
        <p className="kanban-settings-subtitle">
          Controle o comportamento dos quadros Kanban de Vendas e Pós-Vendas.
        </p>
      </div>

      {error && <div className="kanban-settings-error">{error}</div>}
      {successMsg && <div className="kanban-settings-success">{successMsg}</div>}
      {labelsError && <div className="kanban-settings-error">{labelsError}</div>}
      {labelsSuccessMsg && <div className="kanban-settings-success">{labelsSuccessMsg}</div>}

      <section className="kanban-settings-section">
        <div className="kanban-settings-section-header">
          <h3>Pós-Vendas — Mensagem recebida em card finalizado</h3>
          <p>
            Quando chega uma nova mensagem no WhatsApp de um cliente cujo card está na coluna
            <strong> Finalizado </strong>
            do Kanban de Pós-Vendas, escolha o que deve acontecer:
          </p>
        </div>

        <div className="kanban-settings-options">
          <label className={`kanban-settings-option ${route === 'vendas_aguardando_contato' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="finalizadoInboundRoute"
              value="vendas_aguardando_contato"
              checked={route === 'vendas_aguardando_contato'}
              onChange={() => setRoute('vendas_aguardando_contato')}
              disabled={!canEdit || saving}
            />
            <div className="kanban-settings-option-body">
              <div className="kanban-settings-option-title">Criar nova lead no Kanban de Vendas</div>
              <div className="kanban-settings-option-desc">
                Comportamento padrão. O card finalizado permanece no histórico e uma nova lead é
                criada na coluna inicial do Kanban de Vendas para que o time possa atender a nova
                conversa do zero.
              </div>
            </div>
          </label>

          <label className={`kanban-settings-option ${route === 'pos_vendas_em_atendimento' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="finalizadoInboundRoute"
              value="pos_vendas_em_atendimento"
              checked={route === 'pos_vendas_em_atendimento'}
              onChange={() => setRoute('pos_vendas_em_atendimento')}
              disabled={!canEdit || saving}
            />
            <div className="kanban-settings-option-body">
              <div className="kanban-settings-option-title">
                Reativar o mesmo card em Pós-Vendas → Em Atendimento
              </div>
              <div className="kanban-settings-option-desc">
                O card sai da coluna Finalizado e volta para <strong>Em Atendimento</strong> no
                próprio Kanban de Pós-Vendas. O histórico da conversa é preservado e nenhuma nova
                lead é criada no Kanban de Vendas.
              </div>
            </div>
          </label>
        </div>

        <div className="kanban-settings-actions">
          <button
            type="button"
            className="kanban-settings-save"
            onClick={handleSave}
            disabled={!canEdit || !dirty || saving}
          >
            {saving ? 'Salvando…' : 'Salvar alterações'}
          </button>
          {!canEdit && (
            <span className="kanban-settings-readonly">
              Apenas owners ou administradores podem alterar estas configurações.
            </span>
          )}
        </div>
      </section>

      {/* ── Nomes das colunas ── */}
      <section className="kanban-settings-section">
        <div className="kanban-settings-section-header">
          <h3>Kanban de Vendas — Nomes das Colunas</h3>
          <p>Personalize os rótulos exibidos em cada coluna do Kanban de Vendas. Deixe em branco para usar o nome padrão.</p>
        </div>
        <div className="kanban-column-labels-grid">
          {([
            { key: 'aguardandoContato', label: 'Aguardando Contato', defaultVal: VENDAS_DEFAULTS.aguardandoContato },
            { key: 'emAtendimento', label: 'Em Atendimento', defaultVal: VENDAS_DEFAULTS.emAtendimento },
            { key: 'emNegociacao', label: 'Em Negociação', defaultVal: VENDAS_DEFAULTS.emNegociacao },
            { key: 'urgentes', label: 'Urgente', defaultVal: VENDAS_DEFAULTS.urgentes },
            { key: 'proposta', label: 'Proposta', defaultVal: VENDAS_DEFAULTS.proposta },
            { key: 'fechamento', label: 'Fechamento', defaultVal: VENDAS_DEFAULTS.fechamento },
          ] as { key: keyof LabelsFormState['vendas']; label: string; defaultVal: string }[]).map(({ key, label, defaultVal }) => (
            <div key={key} className="kanban-column-label-field">
              <label className="kanban-column-label-field-label">{label}</label>
              <input
                type="text"
                className="kanban-column-label-input"
                value={labels.vendas[key]}
                placeholder={defaultVal}
                maxLength={60}
                disabled={!canEdit || savingLabels}
                onChange={(e) => setVendasLabel(key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="kanban-settings-section">
        <div className="kanban-settings-section-header">
          <h3>Kanban de Pós-Vendas — Nomes das Colunas</h3>
          <p>Personalize os rótulos exibidos em cada coluna do Kanban de Pós-Vendas. Deixe em branco para usar o nome padrão.</p>
        </div>
        <div className="kanban-column-labels-grid">
          {([
            { key: 'conversoesGanhas', label: 'Conversões Ganhas', defaultVal: POS_VENDAS_DEFAULTS.conversoesGanhas },
            { key: 'emAtendimento', label: 'Em Atendimento', defaultVal: POS_VENDAS_DEFAULTS.emAtendimento },
            { key: 'aguardandoRetorno', label: 'Aguardando Retorno', defaultVal: POS_VENDAS_DEFAULTS.aguardandoRetorno },
            { key: 'finalizado', label: 'Finalizado', defaultVal: POS_VENDAS_DEFAULTS.finalizado },
          ] as { key: keyof LabelsFormState['posVendas']; label: string; defaultVal: string }[]).map(({ key, label, defaultVal }) => (
            <div key={key} className="kanban-column-label-field">
              <label className="kanban-column-label-field-label">{label}</label>
              <input
                type="text"
                className="kanban-column-label-input"
                value={labels.posVendas[key]}
                placeholder={defaultVal}
                maxLength={60}
                disabled={!canEdit || savingLabels}
                onChange={(e) => setPosVendasLabel(key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="kanban-settings-section">
        <div className="kanban-settings-section-header">
          <h3>Kanban de Campanhas — Nomes das Colunas</h3>
          <p>Personalize os rótulos exibidos em cada coluna do Kanban de Campanhas. Deixe em branco para usar o nome padrão.</p>
        </div>
        <div className="kanban-column-labels-grid">
          {([
            { key: 'pendente', label: 'Pendente', defaultVal: CAMPANHAS_DEFAULTS.pendente },
            { key: 'emExecucao', label: 'Em Execução', defaultVal: CAMPANHAS_DEFAULTS.emExecucao },
            { key: 'finalizado', label: 'Finalizado', defaultVal: CAMPANHAS_DEFAULTS.finalizado },
          ] as { key: keyof LabelsFormState['campanhas']; label: string; defaultVal: string }[]).map(({ key, label, defaultVal }) => (
            <div key={key} className="kanban-column-label-field">
              <label className="kanban-column-label-field-label">{label}</label>
              <input
                type="text"
                className="kanban-column-label-input"
                value={labels.campanhas[key]}
                placeholder={defaultVal}
                maxLength={60}
                disabled={!canEdit || savingLabels}
                onChange={(e) => setCampanhasLabel(key, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div className="kanban-settings-actions">
          <button
            type="button"
            className="kanban-settings-save"
            onClick={handleSaveLabels}
            disabled={!canEdit || !labelsDirty || savingLabels}
          >
            {savingLabels ? 'Salvando…' : 'Salvar nomes das colunas'}
          </button>
          {!canEdit && (
            <span className="kanban-settings-readonly">
              Apenas owners ou administradores podem alterar estas configurações.
            </span>
          )}
        </div>
      </section>
    </div>
  );
};
